import React from 'react';
import reactStringReplace from 'react-string-replace';
import {
  type LanguageSource,
  cleanLang,
  getLanguageFromLocalStorage,
  getLanguageSource,
  getPreferredLanguage,
  loadDateLocale,
  loadTranslations,
  saveLanguageToLocalStorage,
} from 'utils/i18n';
import { create } from 'zustand';

async function savePreferredLanguageToBackend(lang: string): Promise<void> {
  if (typeof URL_API_HOST === 'undefined') {
    return;
  }

  try {
    const response = await fetch(
      `${URL_API_HOST}/api/v1/languages/preferred/${lang}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      console.error(
        `Error saving preferred language to ${lang}. Status: ${response.status}`,
      );
    }
  } catch (error) {
    console.error('Exception saving preferred language:', error);
  }
}

type Translations = Record<string, string>;

interface I18nState {
  lang: string;
  loading: boolean;
  isInitialized: boolean;
  translations: Translations;
  dateLocale: any;
  languageSource: LanguageSource;
  setLang: (lang: string, source?: string) => Promise<void>;
  requirePathUpdate?: boolean;
  userInitiatedSwitch: boolean;
  load: (lang?: string) => Promise<void>;
  t: (key: string, defaultText?: string) => string;
  translate: (
    key: string,
    replacement?: Record<string, any>,
    options?: { textOnly?: boolean; defaultValue?: string | JSX.Element },
  ) => string | JSX.Element;
}

export const useI18n = create<I18nState>((set, get) => ({
  lang: getPreferredLanguage(),
  loading: false,
  isInitialized: false,
  translations: {},
  dateLocale: null,
  languageSource: 'default',
  userInitiatedSwitch: false,
  setLang: async (preferredLanguage: string, source?: string) => {
    const isUserSwitch = source === 'selector';
    set({ loading: true, userInitiatedSwitch: isUserSwitch });
    const currentLang = get().lang;
    const lang = cleanLang(preferredLanguage);

    if (lang !== currentLang) {
      void savePreferredLanguageToBackend(lang);
    }

    saveLanguageToLocalStorage(lang);

    set({
      lang,
      languageSource: getLanguageSource(lang),
      loading: false,
    });

    // Also update FiltersStore lang to trigger search refetch
    const flux = typeof window !== 'undefined' ? (window as any).flux : null;
    flux?.actions?.filters?.setFilterLang?.(lang);

    await get().load(lang);

    if (isUserSwitch) {
      set({ userInitiatedSwitch: false });
    }
  },
  load: async (langParam?: string) => {
    set({ loading: true });
    const lang = cleanLang(langParam || get().lang || getPreferredLanguage());
    if (lang !== getLanguageFromLocalStorage()) {
      saveLanguageToLocalStorage(lang);
    }
    try {
      const [localeMod, translations] = await Promise.all([
        loadDateLocale(lang),
        process.env.NODE_ENV === 'test'
          ? Promise.resolve({})
          : loadTranslations(lang),
      ]);

      const dateLocaleMod = localeMod as any;
      const dateLocale = (dateLocaleMod &&
        (dateLocaleMod.default ?? dateLocaleMod)) as any;
      set({
        lang,
        translations: (translations || {}) as Translations,
        dateLocale,
        loading: false,
        isInitialized: true,
        languageSource: getLanguageSource(lang),
      });
    } catch (e) {
      if (lang !== 'en') {
        await get().load('en');
        return;
      }
      set({ loading: false });
    }
  },
  t: (key: string, defaultText?: string) => {
    const { translate } = get();
    return translate(
      key,
      {},
      { textOnly: true, defaultValue: defaultText },
    ) as string;
  },
  translate: (
    translateKey: string,
    replacement: Record<string, any> = {},
    options?: { textOnly?: boolean; defaultValue?: string | JSX.Element },
  ): string | JSX.Element => {
    const { translations, lang } = get();
    const textOnly = options?.textOnly;
    const defaultValue = options?.defaultValue;

    let text: any = translations?.[translateKey] || '';

    if (!text) {
      const msg = `[Translator] Could not translate key: ${translateKey}`;
      if ((window as any).debugTranslations) {
        if (window.location.hostname !== 'www.withlocals.com') {
          console.warn(msg);
        } else {
          console.trace(msg);
        }
      }
      if (defaultValue) {
        text = defaultValue;
      }
    }

    let replacementIndex = 0;
    for (const key in replacement) {
      if (!Object.prototype.hasOwnProperty.call(replacement, key)) continue;
      replacementIndex++;
      const replacementKey = `{${key}}`;
      const indexBase = replacementIndex;
      text = reactStringReplace(text, replacementKey, (match, i) => {
        const replaceResult = replacement[key];
        if (
          typeof replaceResult === 'object' &&
          React.isValidElement(replaceResult)
        ) {
          return React.cloneElement<any>(replaceResult, {
            key: `${indexBase}-${i}-${lang}-${translateKey}`,
          });
        }
        return replaceResult;
      });
    }

    if (textOnly) {
      if (Array.isArray(text)) return text.join('');
      return text;
    }

    return (
      <span key={`${lang}-${translateKey}`} data-translatekey={translateKey}>
        {text}
      </span>
    );
  },
}));

let initialized = false;
export function ensureI18nInitialized() {
  if (!initialized) {
    initialized = true;
    void useI18n.getState().load();
  }
}

ensureI18nInitialized();

// Reactive selectors for frequently used substates
export const useLang = () => useI18n((state) => state.lang);
